import tinymce from 'tinymce';

// Set the base URL for TinyMCE's static assets (skins, icons, themes, models, plugins)
tinymce.baseURL = '/vendor/tinymce';

// File browser implementation
window.TinyMceFileBrowser = {
    callback: null,
    meta: null,

    open: function(callback, value, meta) {
        this.callback = callback;
        this.meta = meta;

        var browseUrl = '/uploads/tinymce/browse';
        var fileType = meta.filetype || 'image';
        var self = this;

        fetch(browseUrl + '?type=' + fileType)
            .then(function(r) { return r.json(); })
            .then(function(files) { self.renderBrowser(files, fileType); });
    },

    renderBrowser: function(files, fileType) {
        var self = this;
        var overlay = document.createElement('div');
        overlay.className = 'tinymce-file-browser';

        var html = '<div class="tinymce-file-browser-inner">';
        html += '<div class="tinymce-file-browser-header"><h3>Select a file</h3><button class="tinymce-fb-close">&times;</button></div>';
        html += '<div class="tinymce-file-browser-body"><div class="tinymce-file-browser-grid">';

        files.forEach(function(file, i) {
            html += '<div class="tinymce-file-item" data-url="' + file.url + '" data-index="' + i + '">';
            if (file.type === 'image') {
                html += '<img src="' + file.url + '" alt="' + file.name + '" loading="lazy">';
            } else if (file.type === 'video') {
                html += '<video src="' + file.url + '" muted></video>';
            } else {
                html += '<div class="file-icon"><i class="fa-solid fa-file"></i></div>';
            }
            html += '<div class="file-name">' + file.name + '</div>';
            html += '</div>';
        });

        html += '</div></div>';
        html += '<div class="tinymce-file-browser-footer">';
        html += '<button class="tinymce-fb-close">Cancel</button>';
        html += '<button class="btn-primary tinymce-fb-select" disabled>Select</button>';
        html += '</div></div>';

        overlay.innerHTML = html;
        document.body.appendChild(overlay);

        var selectedUrl = null;
        overlay.querySelectorAll('.tinymce-file-item').forEach(function(item) {
            item.addEventListener('click', function() {
                overlay.querySelectorAll('.tinymce-file-item').forEach(function(el) { el.classList.remove('selected'); });
                item.classList.add('selected');
                selectedUrl = item.getAttribute('data-url');
                overlay.querySelector('.tinymce-fb-select').disabled = false;
            });
            item.addEventListener('dblclick', function() {
                selectedUrl = item.getAttribute('data-url');
                self.selectFile(selectedUrl, overlay);
            });
        });

        overlay.querySelector('.tinymce-fb-select').addEventListener('click', function() {
            if (selectedUrl) self.selectFile(selectedUrl, overlay);
        });

        overlay.querySelectorAll('.tinymce-fb-close').forEach(function(btn) {
            btn.addEventListener('click', function() { overlay.remove(); });
        });

        overlay.addEventListener('click', function(e) {
            if (e.target === overlay) overlay.remove();
        });
    },

    selectFile: function(url, overlay) {
        if (this.callback) {
            this.callback(url, { title: url.split('/').pop() });
        }
        overlay.remove();
    }
};

function getUploadHandler(uploadUrl) {
    return function(blobInfo) {
        return new Promise(function(resolve, reject) {
            var csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
            var formData = new FormData();
            formData.append('file', blobInfo.blob(), blobInfo.filename());
            formData.append('_token', csrfToken);

            fetch(uploadUrl, {
                method: 'POST',
                body: formData,
                headers: { 'X-CSRF-TOKEN': csrfToken }
            })
            .then(function(response) {
                if (!response.ok) throw new Error('Upload failed');
                return response.json();
            })
            .then(function(data) { resolve(data.location); })
            .catch(function(err) { reject('Upload failed: ' + err.message); });
        });
    };
}

function filePickerCallback(callback, value, meta) {
    if (meta.filetype === 'image' || meta.filetype === 'media' || meta.filetype === 'file') {
        window.TinyMceFileBrowser.open(callback, value, meta);
    }
}

// Initialize standard (textarea) TinyMCE editors
function initEditors() {
    document.querySelectorAll('.tinymce-editor').forEach(function(el) {
        if (el.classList.contains('tinymce-initialized')) return;
        el.classList.add('tinymce-initialized');

        var uploadUrl = el.getAttribute('data-tinymce-upload-url') || '/uploads/tinymce';

        tinymce.init({
            target: el,
            license_key: 'gpl',
            skin_url: '/vendor/tinymce/skins/ui/tinymce-5-dark',
            content_css: '/vendor/tinymce/skins/content/dark/content.min.css',
            plugins: 'advlist autolink lists link image media table code fullscreen wordcount help',
            toolbar: 'undo redo | blocks | bold italic underline strikethrough | link image media | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | table | code fullscreen | removeformat help',
            menubar: 'file edit view insert format tools table help',
            branding: false,
            promotion: false,
            height: 400,
            convert_urls: false,
            // Allow <i> with class/style even when empty (needed for FontAwesome icons)
            // Allow <style> blocks within the editor HTML
            extended_valid_elements: 'i[class|style|aria-hidden|data-*],style[*]',
            valid_children: '+body[style]',
            content_style: 'body { background-color: #262626; }',
            images_upload_handler: getUploadHandler(uploadUrl),
            file_picker_callback: filePickerCallback,
            file_picker_types: 'image media file',
        });
    });
}

// Initialize inline TinyMCE editors (for content block inline editing)
window.TinyMceInlineInit = function(target, options) {
    var csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';

    var defaults = {
        target: target,
        inline: true,
        license_key: 'gpl',
        skin_url: '/vendor/tinymce/skins/ui/tinymce-5-dark',
        content_css: '/vendor/tinymce/skins/content/dark/content.min.css',
        plugins: 'advlist autolink lists link image media table code fullscreen wordcount',
        toolbar: 'undo redo | blocks | bold italic underline strikethrough | link image media | alignleft aligncenter alignright | bullist numlist | table | code | removeformat',
        menubar: false,
        branding: false,
        promotion: false,
        convert_urls: false,
        // Allow <i> with class/style even when empty (needed for FontAwesome icons)
        // Allow <style> blocks within the editor HTML
        extended_valid_elements: 'i[class|style|aria-hidden|data-*],style[*]',
        valid_children: '+body[style]',
        images_upload_handler: getUploadHandler('/uploads/tinymce'),
        file_picker_callback: filePickerCallback,
        file_picker_types: 'image media file',
    };

    return tinymce.init(Object.assign(defaults, options || {}));
};

// Expose tinymce globally for inline editing cleanup
window.tinymce = tinymce;

document.addEventListener('DOMContentLoaded', initEditors);
